import { APIService } from '@/services/api';

const NAMESPACE = import.meta.env.VITE_NAMESPACE;
const ADMIN_BACKUP_TOKEN_KEY = `bolzai_${NAMESPACE}_admin_backup_token`;
const IMPERSONATED_USER_KEY = `bolzai_${NAMESPACE}_impersonated_user`;

interface ImpersonatedUserInfo {
  name: string;
}

/**
 * Start impersonating a user.
 * Backs up the current admin token and sets the new user token.
 *
 * @param newToken - The token of the user to impersonate
 * @param userName - The display name of the user being impersonated
 */
export function startImpersonation(newToken: string, userName: string): void {
  // Backup current admin token
  const currentToken = APIService.inst.getToken();
  if (currentToken !== null) {
    localStorage.setItem(ADMIN_BACKUP_TOKEN_KEY, currentToken);
  }

  // Store impersonated user info
  const userInfo: ImpersonatedUserInfo = { name: userName };
  localStorage.setItem(IMPERSONATED_USER_KEY, JSON.stringify(userInfo));

  // Set the new token
  APIService.inst.setToken(newToken);
}

/**
 * End the current impersonation session.
 * Restores the admin token and clears impersonation data.
 *
 * @returns true if impersonation was ended successfully, false if not impersonating
 */
export function endImpersonation(): boolean {
  const backupToken = localStorage.getItem(ADMIN_BACKUP_TOKEN_KEY);

  if (backupToken === null) {
    return false;
  }

  // Restore admin token
  APIService.inst.setToken(backupToken);

  // Clear impersonation data
  localStorage.removeItem(ADMIN_BACKUP_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATED_USER_KEY);

  return true;
}

/**
 * Check if the current session is an impersonation session.
 *
 * @returns true if currently impersonating a user
 */
export function isImpersonating(): boolean {
  return localStorage.getItem(ADMIN_BACKUP_TOKEN_KEY) !== null;
}

/**
 * Get the display name of the currently impersonated user.
 *
 * @returns The user's display name, or null if not impersonating
 */
export function getImpersonatedUserName(): string | null {
  const userInfoJson = localStorage.getItem(IMPERSONATED_USER_KEY);

  if (userInfoJson === null) {
    return null;
  }

  try {
    const userInfo: ImpersonatedUserInfo = JSON.parse(userInfoJson);
    return userInfo.name;
  } catch {
    return null;
  }
}

/**
 * Clear all impersonation data without restoring the token.
 * Used during logout to ensure clean state.
 */
export function clearImpersonationData(): void {
  localStorage.removeItem(ADMIN_BACKUP_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATED_USER_KEY);
}
